<template>
  <div>
    <slot :files />
    <input
      ref="inputRef"
      v-bind="{ multiple: true, hidden: true }"
      :accept="accept?.join(',')"
      type="file"
      @change="onChange"
    >
  </div>
</template>

<script setup lang="ts">
import { FileUploadContextKey } from './context'

const {
  accept,
  maxFileSize = Number.POSITIVE_INFINITY,
  maxFiles = 1,
} = defineProps<{
  /**
   * The accept file types
   */
  accept?: string[]
  /**
   * The maximum file size in bytes
   */
  maxFileSize?: number
  /**
   * The maximum number of files
   */
  maxFiles?: number
}>()

const emit = defineEmits<{
  change: [reason?: 'accept' | 'maxFileSize' | 'maxFiles']
}>()

defineSlots<{
  default: (props: { files: File[] }) => void
}>()

const model = defineModel<File[]>({ default: [] })

const files = ref<File[]>([])
const inputRef = ref<HTMLInputElement | null>(null)

provide(FileUploadContextKey, {
  inputRef,
  files,
  onDelete
})

function onDelete(file: File) {
  files.value = files.value.filter((f) => f !== file)
  const dt = new DataTransfer()
  files.value.forEach((f) => dt.items.add(f))
  inputRef.value!.files = dt.files
  model.value = files.value
}

function onChange(event: Event) {
  const target = event.target as HTMLInputElement

  if (!target.files?.length) return

  files.value = [...files.value, ...target.files]

  if (files.value.some((file) => !accept?.includes(file.type))) {
    emit('change', 'accept')
    reset()
    return
  }

  if (files.value.length > maxFiles) {
    emit('change', 'maxFiles')
    reset()
    return
  }

  if (files.value.some((file) => (file.size > maxFileSize))) {
    emit('change', 'maxFileSize')
    reset()
    return
  }

  emit('change')
  model.value = files.value
}

function reset() {
  files.value = []
  inputRef.value!.value = ''
  model.value = files.value
}
</script>
